'use client';

/**
 * AuditLogTable Component
 *
 * Tabular view of audit logs with sorting, filtering, pagination,
 * and export capabilities for power users.
 *
 * Built on top of DataTable component for consistent table behavior.
 *
 * @see Issue #234 - Entity Audit Activity Logs
 */

import DownloadIcon from '@mui/icons-material/Download';
import FilterIcon from '@mui/icons-material/FilterList';
import ViewIcon from '@mui/icons-material/Visibility';
import { Box, Chip, IconButton, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import type { AuditEntry, AuditExportConfig, AuditLogTableProps, AuditSeverity, AuditStatus } from '../../types/audit';
import type { DataTableColumn } from '../../types/dataTable';
import { DataTable } from '../DataTable/DataTable';
import { GlassCard } from '../GlassCard/GlassCard';
import { GlassTooltip } from '../GlassTooltip';

// ============================================================================
// Helper Functions
// ============================================================================

function getStatusColor(status: AuditStatus): 'success' | 'error' | 'warning' | 'default' {
  switch (status) {
    case 'success':
      return 'success';
    case 'failure':
      return 'error';
    case 'pending':
      return 'warning';
    default:
      return 'default';
  }
}

function getSeverityColor(severity?: AuditSeverity): 'error' | 'warning' | 'info' | 'default' {
  switch (severity) {
    case 'critical':
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
    default:
      return 'default';
  }
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ============================================================================
// Column Definitions
// ============================================================================

function createDefaultColumns(
  onRowClick?: (entry: AuditEntry) => void,
  t: (key: string) => string = (k) => k,
): DataTableColumn<AuditEntry>[] {
  return [
    {
      id: 'createdAt',
      header: t('audit.table.columns.timestamp'),
      accessor: 'createdAt',
      sortable: true,
      width: 150,
      cell: (value) => formatDateTime(value as string),
    },
    {
      id: 'actionType',
      header: t('audit.table.columns.action'),
      accessor: 'actionType',
      sortable: true,
      width: 100,
      cell: (value) => (
        <Chip label={value as string} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
      ),
    },
    {
      id: 'resourceType',
      header: t('audit.table.columns.resource'),
      accessor: 'resourceType',
      sortable: true,
      width: 100,
      cell: (value) => <Typography variant="body2">{(value as string) || '-'}</Typography>,
    },
    {
      id: 'userName',
      header: t('audit.table.columns.user'),
      accessor: 'userName',
      sortable: true,
      width: 150,
      cell: (value, row) => (
        <Box>
          <Typography variant="body2">{(value as string) || t('common.system')}</Typography>
          {row.userEmail && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {row.userEmail}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'status',
      header: t('audit.table.columns.status'),
      accessor: 'status',
      sortable: true,
      width: 80,
      align: 'center',
      cell: (value) => <Chip label={value as string} size="small" color={getStatusColor(value as AuditStatus)} />,
    },
    {
      id: 'severity',
      header: t('audit.table.columns.severity'),
      accessor: 'severity',
      width: 80,
      align: 'center',
      cell: (value) =>
        value ? (
          <Chip
            label={value as string}
            size="small"
            color={getSeverityColor(value as AuditSeverity)}
            variant="outlined"
          />
        ) : (
          '-'
        ),
    },
    {
      id: 'ipAddress',
      header: t('audit.table.columns.ipAddress'),
      accessor: 'ipAddress',
      width: 120,
      cell: (value) => <Typography variant="body2">{(value as string) || '-'}</Typography>,
    },
    {
      id: 'actions',
      header: t('common.actions'),
      accessor: 'id',
      sortable: false,
      width: 50,
      align: 'center',
      cell: (_, row) => (
        <GlassTooltip title={t('common.view')}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRowClick?.(row);
            }}
          >
            <ViewIcon fontSize="small" />
          </IconButton>
        </GlassTooltip>
      ),
    },
  ];
}

// ============================================================================
// Component
// ============================================================================

export function AuditLogTable({
  entries,
  loading = false,
  error,
  page = 0,
  pageSize = 25,
  totalRows = 0,
  onPageChange,
  onPageSizeChange,
  sortColumn,
  sortDirection = 'desc',
  onSortChange,
  searchValue = '',
  onSearchChange,
  onRowClick,
  onExport,
  visibleColumns,
  variant = 'glass',
  neonAccent,
  density = 'normal',
  title,
}: AuditLogTableProps) {
  const { t } = useI18n();
  const [localSearch, setLocalSearch] = useState(searchValue);

  // Create columns with visibility filter
  const columns = useMemo(() => {
    const allColumns = createDefaultColumns(onRowClick, t);
    if (!visibleColumns) return allColumns;
    // Always include actions column, filter others by visibility
    return allColumns.filter((col) => col.id === 'actions' || visibleColumns.includes(col.id));
  }, [visibleColumns, onRowClick, t]);

  // Handle search
  const handleSearch = (value: string) => {
    setLocalSearch(value);
    onSearchChange?.(value);
  };

  // Handle export
  const handleExport = async (format: 'csv' | 'json') => {
    if (!onExport) return;
    const config: AuditExportConfig = {
      format,
      fileName: `audit-logs-${new Date().toISOString().split('T')[0]}`,
    };
    await onExport(config);
  };

  // Handle sort change - adapt DataTable's sort format to our prop format
  const handleSortChange = (sort?: { column: string; direction: 'asc' | 'desc' }) => {
    if (onSortChange && sort) {
      onSortChange(sort.column, sort.direction);
    }
  };

  // Handle pagination change - adapt DataTable's pagination format to our props
  const handlePaginationChange = (pagination: { page: number; pageSize: number }) => {
    if (onPageChange && pagination.page !== page) {
      onPageChange(pagination.page);
    }
    if (onPageSizeChange && pagination.pageSize !== pageSize) {
      onPageSizeChange(pagination.pageSize);
    }
  };

  // Custom toolbar with title, filter, and export buttons
  const toolbar = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {title && (
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mr: 'auto',
          }}
        >
          {title}
        </Typography>
      )}
      {/* Filter button placeholder */}
      <GlassTooltip title={t('audit.filter.action')}>
        <IconButton size="small">
          <FilterIcon fontSize="small" />
        </IconButton>
      </GlassTooltip>
      {/* Export */}
      {onExport && (
        <GlassTooltip title={t('audit.table.export')}>
          <IconButton size="small" onClick={() => handleExport('csv')}>
            <DownloadIcon fontSize="small" />
          </IconButton>
        </GlassTooltip>
      )}
    </Box>
  );

  // Empty state content
  const emptyState = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        textAlign: 'center',
      }}
    >
      <Typography
        variant="body1"
        sx={{
          color: 'text.secondary',
        }}
      >
        {t('audit.timeline.empty')}
      </Typography>
    </Box>
  );

  const table = (
    <DataTable<AuditEntry>
      data={entries}
      columns={columns}
      getRowId={(entry) => entry.id}
      loading={loading}
      error={error}
      // Sorting
      sortable={!!onSortChange}
      sort={sortColumn ? { column: sortColumn, direction: sortDirection } : undefined}
      onSortChange={handleSortChange}
      // Pagination
      paginated={!!(onPageChange || onPageSizeChange)}
      pagination={{
        page,
        pageSize,
        pageSizeOptions: [10, 25, 50, 100],
      }}
      onPaginationChange={handlePaginationChange}
      // Server-side mode for controlled pagination
      serverSide
      totalRows={totalRows}
      // Filtering
      filterable={!!onSearchChange}
      searchValue={localSearch}
      onSearchChange={handleSearch}
      // Row interaction
      onRowClick={onRowClick}
      // Appearance
      variant={variant}
      neonAccent={neonAccent}
      density={density}
      hoverable
      // Custom elements
      toolbar={toolbar}
      emptyState={emptyState}
    />
  );

  // Wrap in card if glass variant (DataTable handles its own styling, but we may want wrapper)
  if (variant === 'glass') {
    return <GlassCard neonAccent={neonAccent}>{table}</GlassCard>;
  }

  return table;
}

AuditLogTable.displayName = 'AuditLogTable';

export default AuditLogTable;
